/*
 * Where a photo was taken, taken out of it before anyone else can read it. A phone writes the place
 * into a JPEG's EXIF as its GPS block, and some editors copy it into the XMP too. `withoutLocation`
 * takes both out and leaves everything else, the orientation above all, since a photo without it
 * shows on its side. It works on the bytes alone, so it is the same in any browser and in a test.
 */

/** A file's bytes, as a `File` is made from them. */
type Bytes = Uint8Array<ArrayBuffer>

/** EXIF's GPSInfo tag: the pointer from the first IFD to the GPS block. */
const gpsInfo = 0x8825

/** The bytes in one value of each TIFF field type, by the type's number. */
const typeSizes: Record<number, number> = {
  1: 1,
  2: 1,
  3: 2,
  4: 4,
  5: 8,
  6: 1,
  7: 1,
  8: 2,
  9: 4,
  10: 8,
  11: 4,
  12: 8,
}

const ascii = (bytes: Uint8Array) => String.fromCharCode(...bytes)

const exifHeader = "Exif\0\0"
const xmpHeaders = ["http://ns.adobe.com/xap/1.0/\0", "http://ns.adobe.com/xmp/extension/\0"]

/**
 * A TIFF structure's reader and writer, in its own byte order, over `tiff` (a copy, so writing to
 * it changes nothing the caller holds). Every read is checked against the end, and throws past it.
 */
function tiffOf(tiff: Uint8Array) {
  const view = new DataView(tiff.buffer, tiff.byteOffset, tiff.byteLength)
  const order = ascii(tiff.subarray(0, 2))
  if (order !== "II" && order !== "MM") throw new Error("not a TIFF structure")
  const little = order === "II"
  const within = (at: number, length: number) => {
    if (at < 0 || at + length > tiff.length) throw new Error("past the end")
    return at
  }
  return {
    u16: (at: number) => view.getUint16(within(at, 2), little),
    u32: (at: number) => view.getUint32(within(at, 4), little),
    setU16: (at: number, value: number) => view.setUint16(within(at, 2), value, little),
    setU32: (at: number, value: number) => view.setUint32(within(at, 4), value, little),
    clear: (at: number, length: number) => tiff.fill(0, within(at, length), at + length),
    /** Moves the bytes from `from` to `end` back to `to`. */
    move: (to: number, from: number, end: number) =>
      tiff.copyWithin(within(to, end - from), within(from, end - from), end),
  }
}

/**
 * An EXIF block's TIFF structure with its GPS block gone: every GPS field and what it points at
 * zeroed, and the pointer to it taken out of the first IFD, the entries after it moving up so
 * nothing else moves. None when it holds no GPS block; throws when it can't be read.
 */
function tiffWithoutGps(source: Bytes): Bytes | undefined {
  const tiff = source.slice()
  const read = tiffOf(tiff)
  const first = read.u32(4)
  const count = read.u16(first)
  const entryAt = (index: number) => first + 2 + index * 12
  let found = -1
  for (let index = 0; index < count; index++)
    if (read.u16(entryAt(index)) === gpsInfo) found = index
  if (found < 0) return undefined

  const gps = read.u32(entryAt(found) + 8)
  const fields = read.u16(gps)
  for (let index = 0; index < fields; index++) {
    const at = gps + 2 + index * 12
    const bytes = (typeSizes[read.u16(at + 2)] ?? 1) * read.u32(at + 4)
    if (bytes > 4) read.clear(read.u32(at + 8), bytes)
  }
  read.clear(gps, 2 + fields * 12 + 4)

  const next = read.u32(entryAt(count))
  read.move(entryAt(found), entryAt(found + 1), entryAt(count))
  read.setU16(first, count - 1)
  read.setU32(entryAt(count - 1), next)
  read.clear(entryAt(count - 1) + 4, 12)
  return tiff
}

/** A JPEG segment: its marker, where it starts and ends in the file, and where its data starts. */
type Segment = { marker: number; start: number; data: number; end: number }

/** The segments ahead of a JPEG's image data, in order, or none when the bytes aren't a JPEG. */
function segmentsOf(bytes: Uint8Array): Segment[] | undefined {
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return undefined
  const segments: Segment[] = []
  let at = 2
  while (at + 4 <= bytes.length) {
    if (bytes[at] !== 0xff) return segments
    const marker = bytes[at + 1] ?? 0
    // Fill bytes before a marker, and the markers that carry no length.
    if (marker === 0xff) {
      at += 1
      continue
    }
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) {
      at += 2
      continue
    }
    // From the start of the image data on there is nothing more to read, nor anything to strip.
    if (marker === 0xda || marker === 0xd9) return segments
    const length = ((bytes[at + 2] ?? 0) << 8) | (bytes[at + 3] ?? 0)
    const end = at + 2 + length
    if (length < 2 || end > bytes.length) return segments
    segments.push({ marker, start: at, data: at + 4, end })
    at = end
  }
  return segments
}

/**
 * A JPEG with where it was taken taken out: its EXIF's GPS block cleared and unlinked, and any XMP
 * that names a GPS field dropped. Everything else is kept byte for byte, the orientation and the
 * image itself among them. An EXIF block it can't read is dropped whole, since what it can't read
 * it can't vouch for. Bytes that aren't a JPEG, and a JPEG that says nothing of where it was taken,
 * come back as they were, the same array.
 */
export function withoutLocation(bytes: Bytes): Bytes {
  const segments = segmentsOf(bytes)
  if (!segments) return bytes
  const replaced = new Map<Segment, Bytes | null>()
  for (const segment of segments) {
    if (segment.marker !== 0xe1) continue
    const body = bytes.subarray(segment.data, segment.end)
    const header = ascii(body.subarray(0, 40))
    if (header.startsWith(exifHeader)) {
      try {
        const tiff = tiffWithoutGps(body.subarray(exifHeader.length))
        if (tiff) replaced.set(segment, tiff)
      } catch {
        replaced.set(segment, null)
      }
    } else if (xmpHeaders.some((xmp) => header.startsWith(xmp))) {
      if (new TextDecoder().decode(body).includes("GPS")) replaced.set(segment, null)
    }
  }
  if (replaced.size === 0) return bytes

  const parts: Bytes[] = []
  let from = 0
  for (const [segment, tiff] of replaced) {
    parts.push(bytes.subarray(from, segment.start))
    // A cleared EXIF block keeps its length, so only its TIFF structure changes.
    if (tiff) parts.push(bytes.subarray(segment.start, segment.data + exifHeader.length), tiff)
    from = segment.end
  }
  parts.push(bytes.subarray(from))
  const out = new Uint8Array(parts.reduce((total, part) => total + part.length, 0))
  let at = 0
  for (const part of parts) {
    out.set(part, at)
    at += part.length
  }
  return out
}
